import { createContext, useContext, useRef, type ReactNode } from "react";
import { useStore, type StoreApi } from "zustand";
import { createVerificationStore, useVerificationStore as defaultStore } from "./store";
import type { VerificationState } from "./types";

type VStoreApi = StoreApi<VerificationState>;

const VerificationStoreContext = createContext<VStoreApi | null>(null);

/**
 * SP3 §4 — wraps a keying pane in its OWN verification store instance. The
 * station renders one provider per pane (primary + companion); the standalone
 * VS does NOT use this provider and keeps consuming the default instance.
 */
export function VerificationStoreProvider({
  store,
  children,
}: {
  store?: VStoreApi;
  children: ReactNode;
}) {
  const ref = useRef<VStoreApi | undefined>(undefined);
  if (!ref.current) ref.current = store ?? createVerificationStore();
  return (
    <VerificationStoreContext.Provider value={ref.current}>
      {children}
    </VerificationStoreContext.Provider>
  );
}

/** The store API for the enclosing pane (or the default instance when no
 *  provider is present — the standalone VS path). */
export function useVerificationStoreApi(): VStoreApi {
  return useContext(VerificationStoreContext) ?? defaultStore;
}

/** Selector hook — reads from the enclosing pane's store (or the default). */
export function usePaneStore<T>(selector: (s: VerificationState) => T): T {
  const api = useVerificationStoreApi();
  return useStore(api, selector);
}
